/**
 * SPLITTING WHAT SOMEBODY JUST WROTE FROM THE THREAD THEY WROTE IT UNDER.
 *
 * ── WHY THIS IS WORTH A MODULE ──
 * These threads carry enormous quoted tails. One message in the archive is
 * 50KB of which the new text is 3KB; a customer on the fifth reply is sending
 * us four copies of our own template underneath two lines of question. A
 * timeline that renders the whole body puts those two lines at the top of a
 * screen-and-a-half of history and the reader has to find them, which is
 * precisely the work a conversation console exists to remove.
 *
 * ── IT FINDS THE SEAM; IT DOES NOT SANITISE ──
 * The two halves come back as HTML STRINGS and the caller sanitises each one.
 * Kept apart on purpose: this module's job is one decision ("where does the
 * new text end") and the renderer's is another ("what markup may reach the
 * page"), and a helper that quietly did both would be the one somebody reuses
 * for the half it does badly. See `MessageBody`, which is the only caller and
 * runs DOMPurify over both halves.
 *
 * ── AND IT MUST RUN BEFORE SANITISING, NOT AFTER ──
 * Every marker below is either a tag or an attribute that the sanitiser's
 * allowlist drops: `id` is not in `ALLOWED_ATTR`, so Outlook's
 * `#divRplyFwdMsg` is gone by then, and before this change `blockquote` was
 * not in `ALLOWED_TAGS` either — which means a Gmail quote tail was arriving
 * in the timeline UNWRAPPED and reading as new text. Splitting first is what
 * makes the markers survive to be read.
 *
 * `DOMParser` is safe to point at a hostile string: a document parsed with
 * `text/html` has no browsing context, so nothing in it executes, no resource
 * is fetched and no handler can fire. It is the same mechanism DOMPurify uses
 * underneath to do its own work.
 *
 * ── WHEN IT FINDS NOTHING, IT CHANGES NOTHING ──
 * Both functions return the whole body as `latest` and an empty `quoted` when
 * there is no marker, AND when the marker is at the very start. The second
 * case is the one that matters: a bare forward, or a reply with nothing added
 * above it, is a message whose entire content is the quote — collapsing that
 * by default would hide the message. A heuristic is allowed to miss a tail;
 * it is not allowed to hide a mail.
 */

/**
 * WHERE A MAIL CLIENT PUTS THE START OF THE HISTORY.
 *
 * In document order, first match wins. Each entry is a real client rather
 * than a guess:
 *
 *   `blockquote`            Gmail, Apple Mail, Thunderbird, most of the rest.
 *                           The generic one, and the reason it is first.
 *   `.gmail_quote`          Gmail's own wrapper, which also carries the "On …
 *                           wrote:" attribution ABOVE the blockquote — so
 *                           matching the blockquote alone would leave that
 *                           line stranded at the bottom of the new text.
 *   `#divRplyFwdMsg`        Outlook on the web and desktop.
 *   `#stopSpelling`         the `<hr>` Outlook puts immediately before it.
 *   `.OutlookMessageHeader` older Outlook's "From:/Sent:/To:" header block.
 *   `#OLK_SRC_BODY_SECTION` Outlook for Mac.
 *   `.moz-cite-prefix`      Thunderbird's attribution line.
 *   `.yahoo_quoted`         Yahoo Mail.
 */
const QUOTE_SELECTOR = [
  'blockquote',
  '.gmail_quote',
  '#divRplyFwdMsg',
  '#stopSpelling',
  '.OutlookMessageHeader',
  '#OLK_SRC_BODY_SECTION',
  '.moz-cite-prefix',
  '.yahoo_quoted',
].join(',');

/** Nothing in `latest` but whitespace means the marker was effectively first. */
const isBlank = (text) => !String(text ?? '').trim();

/** The nodes moved into a detached holder, read back as one HTML string. */
const htmlOf = (doc, nodes) => {
  const holder = doc.createElement('div');
  nodes.forEach((node) => holder.append(node));
  return holder.innerHTML;
};

/**
 * `{ latest, quoted }` for an HTML body.
 *
 * THE CUT IS MADE AT A TOP-LEVEL CHILD, not at the marker itself. Apple Mail
 * nests its blockquote two divs deep and Gmail wraps attribution and
 * blockquote together, so cutting at the marker would leave an unbalanced
 * fragment of the wrapper above the fold. Walking up to the body's own child
 * keeps both halves whole.
 */
export function splitQuotedHtml(html) {
  const source = String(html ?? '');
  if (!source.trim()) return { latest: source, quoted: '' };

  const doc = new DOMParser().parseFromString(source, 'text/html');
  const { body } = doc;
  if (!body) return { latest: source, quoted: '' };

  const marker = body.querySelector(QUOTE_SELECTOR);
  if (!marker) return { latest: source, quoted: '' };

  let cut = marker;
  while (cut.parentNode && cut.parentNode !== body) cut = cut.parentNode;

  const children = Array.from(body.childNodes);
  const at = children.indexOf(cut);
  if (at <= 0) return { latest: source, quoted: '' };

  const before = children.slice(0, at);
  if (isBlank(before.map((node) => node.textContent || '').join(''))) {
    return { latest: source, quoted: '' };
  }

  // `before` first: appending MOVES a node, so the later slice has to be read
  // off the array captured above rather than off the body again.
  const latest = htmlOf(doc, before);
  return { latest, quoted: htmlOf(doc, children.slice(at)) };
}

/**
 * WHERE THE HISTORY STARTS IN A PLAIN-TEXT BODY.
 *
 * Which is not a rare case: past ~90 days the `.eml` has aged into Glacier
 * and the archive answers from the knowledge-base sanitiser's copy instead
 * (`source: 'sanitized'`), which is text only. That copy has its quoted
 * history cut already — so these patterns earn their place on the RAW text
 * bodies, which plenty of senders still write.
 *
 * Three languages because the office's mail is in three. The header-block
 * forms (`From:`, `Feladó:`, `От:`) are how a plain-text Outlook reply opens
 * its quote, and they are anchored to the start of a line so a sentence
 * mentioning one of those words is not mistaken for one.
 */
const TEXT_MARKERS = [
  /^\s*-{2,}\s*Original Message\s*-{2,}/i,
  /^\s*-{3,}\s*Forwarded message\s*-{3,}/i,
  /^\s*_{10,}\s*$/,
  /^\s*>/,
  /^\s*(From|Sent|To|Subject):\s/,
  /^\s*(Feladó|Küldte|Dátum|Címzett|Tárgy):\s/i,
  /^\s*(От|Отправлено|Кому|Тема):\s/i,
  /\bwrote:\s*$/,
  /\bírta:\s*$/i,
  /\bнаписал\w*:\s*$/i,
];

/**
 * `{ latest, quoted }` for a plain-text body.
 *
 * `wrote:` is matched at the END of a line rather than anywhere in it,
 * because the attribution Gmail and Apple Mail write — "On 3 Oct 2026, at
 * 14:02, Ada Lovelace <ada@example.com> wrote:" — can be wrapped by the
 * sender's client onto two lines, and the colon is always last on the last of
 * them. A sentence that happens to contain the word does not end on it.
 */
export function splitQuotedText(text) {
  const source = String(text ?? '');
  if (!source.trim()) return { latest: source, quoted: '' };

  const lines = source.split('\n');
  const at = lines.findIndex((line) => TEXT_MARKERS.some((marker) => marker.test(line)));
  if (at <= 0) return { latest: source, quoted: '' };

  const before = lines.slice(0, at).join('\n');
  if (isBlank(before)) return { latest: source, quoted: '' };

  return { latest: before.replace(/\s+$/, ''), quoted: lines.slice(at).join('\n') };
}

export default splitQuotedHtml;
